import { useState, useCallback } from "react";
import { fetchDepartmentsAsync, fetchRolesAsync } from "@features/invitations/services/invitationSvc";
import { Department } from "@features/invitations/types/departmentResponse";
import { Role, RolesQueryParams } from "@features/invitations/types/roleResponse";
import { fetchAllPages } from "@core/utils/fetchAllPages";
import { getApiScopeFromValue } from "@features/invitations/utils/getScopeFromParam";


/**
 * Custom React hook to manage invitation options such as roles and departments.
 *
 * @param options - Optional. When provided, department fetch uses scoped endpoint (church/district/nation).
 * @param options.scope - Scope for department query: 'church' | 'district' | 'nation'
 * @param options.scopeRefId - Entity ID for the scope
 * @returns An object containing fetchRoles, roles, fetchDepartments, departments, etc.
 */
export function useInvitationOptions(options?: { scope?: string; scopeRefId?: number }) {
    const scope = options?.scope;
    const scopeRefId = options?.scopeRefId;
    // Roles state
    const [roles, setRoles] = useState<Role[]>([]);
    const [rolesLoading, setRolesLoading] = useState(false);
    const [rolesError, setRolesError] = useState<string | null>(null);

    // Departments state
    const [departments, setDepartments] = useState<Department[]>([]);
    const [depsLoading, setDepsLoading] = useState(false);
    const [depsError, setDepsError] = useState<string | null>(null);


    /**
     * Fetches the list of departments asynchronously and updates the local state accordingly.
     * Handles loading and error states, and ensures that state updates are not performed if the operation is cancelled.
     *
     * @returns {() => void} A cleanup function that sets the cancelled flag to true, preventing further state updates.
     *
     * @remarks
     * This function is memoized using `useCallback` and is intended to be used within a React component.
     * It relies on the `fetchDepartmentsAsync` function to retrieve department data.
     */
    const fetchDepartments = useCallback(async () => {
        let cancelled = false;

        // Start loading departments
        setDepsLoading(true);

        // Clear previous error
        setDepsError(null);

        try {
            const apiScope = getApiScopeFromValue(scope ?? '');
            const scopeParams =
                apiScope && scopeRefId != null && scopeRefId > 0
                    ? {
                        scope: apiScope,
                        scope_ref_id: scopeRefId,
                    }
                    : {};
            const allItems = await fetchAllPages<Department>((page) =>
                fetchDepartmentsAsync({ all: true, page, isActive: true, ...scopeParams })
            );
            if (!cancelled) {
                setDepartments(allItems);
            }
        } catch (e: any) {
            if (!cancelled) {
                // Set error message if fetching fails
                setDepsError(e?.message ?? "Failed to load departments");
            }
        } finally {
            if (!cancelled) {
                // Stop loading indicator
                setDepsLoading(false);
            }
        }

        // Cleanup function to prevent state updates if the component unmounts
        return () => { cancelled = true; };
    }, [scope, scopeRefId]);

    /**
     * Fetches roles asynchronously based on the provided query parameters.
     * 
     * This function sets the loading state, clears any previous errors, and attempts to fetch roles
     * using the `fetchRolesAsync` function. On success, it updates the roles state with the fetched data.
     * If an error occurs, it sets an error message. The loading state is reset after the operation completes.
     *
     * @param query - The parameters used to query roles.
     */
    const fetchRoles = useCallback(async (query: RolesQueryParams) => {
        // Start loading roles
        setRolesLoading(true);
        // Clear previous error
        setRolesError(null);

        try {
            // Fetch roles based on query parameters
            const response = await fetchRolesAsync(query);
            // Update roles state with fetched data
            setRoles(response.getData()?.data ?? []);
        } catch (e: any) {
            // Set error message if fetching fails
            setRolesError(e?.message ?? "Failed to load roles");
        } finally {
            // Stop loading indicator
            setRolesLoading(false);
        }
    }, []);

    // Return state and fetch functions
    return { fetchRoles, roles, rolesLoading, rolesError, fetchDepartments, departments, depsLoading, depsError };

}